import { Autocomplete, Box, Stack, TextField } from "@mui/material";
import { PAGINATION, PIPELINE_INSTANCE_STATUS } from "../../../constants";
import { formatDateTime, offset } from "../../../utils/formatTime";
import { useEffect, useMemo, useRef, useState } from "react";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import useSWR from "swr";
import { useSnackbar } from "../../../hooks";

const SELECTED_PIPELINE_INSTANCE_PLACEHOLDER =
    "SELECTED_PIPELINE_INSTANCE_UUID";

/**
 * One mission picker per input declared in a job's `anotherDateSelection`.
 * Each input needs data produced by a successful pipeline instance of a
 * *sibling* mission (same site) that computed a given trait group data
 * type (TGDT). This picker replaces the input's normal text field (the
 * input is hidden from `JobNodeInputs`). Picking a mission:
 *   - sets `main.<inputKey>` to the `preExecItem.to` path (the file the
 *     pre-exec step downloads the sibling result into), and
 *   - stores the resolved `preExecItem` at
 *     `preExec.anotherDateSelection.<inputKey>` (with the endpoint's
 *     `SELECTED_PIPELINE_INSTANCE_UUID` placeholder replaced by the picked
 *     mission's most recent successful pipeline instance UUID).
 * Clearing the picker resets both to null.
 *
 * On relaunch, a one-shot reverse lookup pre-selects the mission encoded
 * in `preExec.args[0]`'s `<inputKey>DownloadSelected` (set by a previous
 * run), mirroring `TrainedModelSelector`. `buildUpdatedTranslatedPipeline`
 * reads the stored `preExecItem` back into `preExec.args[0]` at submit.
 *
 * This outer component resolves the current mission's site (the sibling
 * missions all belong to it) and renders one inner selector per input.
 */
AnotherDateSelector.propTypes = {
    jobName: PropTypes.string.isRequired,
    anotherDateSelection: PropTypes.objectOf(
        PropTypes.shape({
            traitGroupDataTypeUuid: PropTypes.string.isRequired,
            preExecItem: PropTypes.shape({
                endpoint: PropTypes.string.isRequired,
                to: PropTypes.string.isRequired,
            }).isRequired,
        })
    ).isRequired,
    preExecArgs: PropTypes.arrayOf(PropTypes.string),
    missionUuid: PropTypes.string,
    experimentUuids: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.bool,
};

export function AnotherDateSelector({
    jobName,
    anotherDateSelection,
    preExecArgs,
    missionUuid,
    experimentUuids,
    disabled,
}) {
    return (
        <FieldsetShell>
            {Object.entries(anotherDateSelection).map(([inputKey, config]) => (
                <AnotherDateInputSelect
                    key={inputKey}
                    jobName={jobName}
                    inputKey={inputKey}
                    config={config}
                    preExecArgs={preExecArgs}
                    currentMissionUuid={missionUuid}
                    experimentUuids={experimentUuids}
                    disabled={disabled}
                />
            ))}
        </FieldsetShell>
    );
}

AnotherDateInputSelect.propTypes = {
    jobName: PropTypes.string.isRequired,
    inputKey: PropTypes.string.isRequired,
    config: PropTypes.shape({
        traitGroupDataTypeUuid: PropTypes.string.isRequired,
        preExecItem: PropTypes.shape({
            endpoint: PropTypes.string.isRequired,
            to: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    preExecArgs: PropTypes.arrayOf(PropTypes.string),
    currentMissionUuid: PropTypes.string,
    experimentUuids: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.bool,
};

function AnotherDateInputSelect({
    jobName,
    inputKey,
    config,
    preExecArgs,
    currentMissionUuid,
    experimentUuids,
    disabled,
}) {
    const { setValue } = useFormContext();
    const { openSnackbar } = useSnackbar();
    const [selectedExperimentMission, setSelectedExperimentMission] =
        useState(null);

    const experimentUuid = experimentUuids?.[0];

    // Sibling experiment missions of the same experiment, with their pipeline instances
    // computing this input's TGDT eager-loaded under
    // `AssociatedTraitGroups[0].PipelineInstances`.
    const { data, error, isLoading } = useSWR(
        experimentUuid
            ? `${BACKEND_ROUTES.EXPERIMENT_MISSION}?experimentUuid=${experimentUuid}` +
                  `&parentInfo=true` +
                  `&pipelineInstancesTGDTUuid=${config.traitGroupDataTypeUuid}` +
                  `&limit=${PAGINATION.GENERIC.LIMIT.MAX}`
            : null
    );

    // Keep only sibling experiment missions (exclude the current one) that have at
    // least one successful pipeline instance for this TGDT.
    const eligibleExperimentMissions = useMemo(
        () =>
            (data?.rows ?? [])
                .filter(
                    (em) =>
                        em.missionUuid !== currentMissionUuid &&
                        getMostRecentSuccessfulPipelineInstance(
                            em,
                            experimentUuids
                        ) != null
                )
                .toSorted((a, b) =>
                    a.Mission.date.localeCompare(b.Mission.date)
                ),
        [data, currentMissionUuid, experimentUuids]
    );

    const applySelection = (experimentMission) => {
        const pipelineInstance = getMostRecentSuccessfulPipelineInstance(
            experimentMission,
            experimentUuids
        );
        const resolvedPreExecItem = {
            ...config.preExecItem,
            endpoint: config.preExecItem.endpoint.replaceAll(
                SELECTED_PIPELINE_INSTANCE_PLACEHOLDER,
                pipelineInstance.uuid
            ),
        };

        setValue(`${jobName}.main.${inputKey}`, config.preExecItem.to);
        setValue(
            `${jobName}.preExec.anotherDateSelection.${inputKey}`,
            resolvedPreExecItem
        );
    };

    const clearSelection = () => {
        setValue(`${jobName}.main.${inputKey}`, null);
        setValue(`${jobName}.preExec.anotherDateSelection.${inputKey}`, null);
    };

    const handleChange = (experimentMission) => {
        setSelectedExperimentMission(experimentMission);
        if (experimentMission) applySelection(experimentMission);
        else clearSelection();
    };

    // One-shot reverse lookup: on relaunch, `preExec.args[0]` may already
    // carry a `<inputKey>DownloadSelected` from a previous run. Decode the
    // pipeline instance UUID it points at, find the experiment mission it belongs to,
    // and pre-select it once the sibling missions have loaded.
    const reverseLookupDoneRef = useRef(false);
    useEffect(() => {
        if (reverseLookupDoneRef.current) return;
        if (!data) return;
        reverseLookupDoneRef.current = true;

        const savedPreExecItem = readSavedPreExecItem(preExecArgs, inputKey);
        if (!savedPreExecItem) return;

        const selectedPiUuid = extractSelectedPiUuid(
            config.preExecItem.endpoint,
            savedPreExecItem.endpoint
        );
        const experimentMission = eligibleExperimentMissions.find((em) =>
            (em.AssociatedTraitGroups ?? [])
                .flatMap((atg) => atg.PipelineInstances ?? [])
                .some((pi) => pi.uuid === selectedPiUuid)
        );

        if (!experimentMission) {
            openSnackbar(
                `${jobName}: previously selected date for "${inputKey}" is no longer available.`,
                "info"
            );
            return;
        }

        setSelectedExperimentMission(experimentMission);
        setValue(`${jobName}.main.${inputKey}`, config.preExecItem.to, {
            shouldDirty: false,
        });
        setValue(
            `${jobName}.preExec.anotherDateSelection.${inputKey}`,
            savedPreExecItem,
            { shouldDirty: false }
        );
    }, [
        data,
        eligibleExperimentMissions,
        preExecArgs,
        inputKey,
        config,
        jobName,
        setValue,
        openSnackbar,
    ]);

    if (error) return <FetchErrorAlert error={error} />;

    return (
        <Autocomplete
            options={eligibleExperimentMissions}
            getOptionLabel={(experimentMission) =>
                formatDateTime(
                    offset(new Date(experimentMission.Mission.date), true)
                )
            }
            isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
            value={selectedExperimentMission}
            loading={isLoading}
            disabled={disabled || !experimentUuid}
            onChange={(_, newValue) => handleChange(newValue)}
            renderInput={(params) => <TextField {...params} label={inputKey} />}
            fullWidth
        />
    );
}

/**
 * Returns an experiment mission's most recent successful pipeline instance for the
 * eager-loaded TGDT, or null. The backend returns `PipelineInstances`
 * most recent first, but we sort by `createdAt` defensively (as
 * `TraitGroupModal` does) rather than rely on the response order.
 *
 * The pipeline instances should also be filtered by the experimentUuids.
 * This means that only the pipeline instances which include all the experimentUuids
 * are kept. With no experiment scope to match against (missing or empty
 * `experimentUuids`), no candidate can be validated, so we return null
 * rather than let `every` vacuously accept every pipeline instance.
 */
function getMostRecentSuccessfulPipelineInstance(
    experimentMission,
    experimentUuids
) {
    if (!experimentUuids?.length) return null;

    const pipelineInstances = (
        experimentMission.AssociatedTraitGroups[0]?.PipelineInstances ?? []
    ).filter((pi) =>
        experimentUuids.every((uuid) =>
            (pi.translateParams?.variables?.experimentUuid ?? []).includes(uuid)
        )
    );
    return (
        pipelineInstances
            .filter((pi) => pi.status === PIPELINE_INSTANCE_STATUS.FINISHED)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] ??
        null
    );
}

/**
 * Reads the resolved `preExecItem` a previous run stored at
 * `<inputKey>DownloadSelected` in `preExec.args[0]`, or null if absent or
 * unparseable (e.g. on a first launch).
 */
function readSavedPreExecItem(preExecArgs, inputKey) {
    if (!preExecArgs || preExecArgs.length === 0) return null;
    let blob;
    try {
        blob = JSON.parse(preExecArgs[0]);
    } catch {
        return null;
    }
    return blob[`${inputKey}DownloadSelected`] ?? null;
}

/**
 * Recovers the pipeline instance UUID that replaced
 * `SELECTED_PIPELINE_INSTANCE_UUID` in a saved endpoint, by diffing it
 * against the template endpoint. Returns null if the saved endpoint does
 * not match the template's shape.
 */
function extractSelectedPiUuid(templateEndpoint, savedEndpoint) {
    if (typeof savedEndpoint !== "string") return null;
    const [prefix, suffix] = templateEndpoint.split(
        SELECTED_PIPELINE_INSTANCE_PLACEHOLDER
    );
    if (
        suffix === undefined ||
        !savedEndpoint.startsWith(prefix) ||
        !savedEndpoint.endsWith(suffix)
    ) {
        return null;
    }
    return savedEndpoint.slice(
        prefix.length,
        savedEndpoint.length - suffix.length
    );
}

FieldsetShell.propTypes = {
    children: PropTypes.node.isRequired,
};

function FieldsetShell({ children }) {
    return (
        <Box
            component="fieldset"
            className="nodrag nopan nowheel"
            sx={{
                border: "2px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                mx: 1,
                my: 1,
                p: 1,
            }}
        >
            <Box
                component="legend"
                sx={{
                    px: 0.5,
                    fontSize: "1rem",
                    color: "text.secondary",
                }}
            >
                Other Date Selection
            </Box>
            <Stack direction="column" spacing={1}>
                {children}
            </Stack>
        </Box>
    );
}
